import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SQUAWK_CONNECT_TIMEOUT_MS,
  ensureSrsSdk,
  getSharedAudioContext,
  resumeSharedAudioContext,
} from './srsConstants';

export type SquawkStatus =
  | 'disconnected'
  | 'connecting'
  | 'live'
  | 'muted'
  | 'error';

export interface UseSquawkStreamOptions {
  /** Fallback webrtc:// URL if getStreamUrl is omitted. */
  streamUrl: string;
  /** Mint authenticated play URL on each start (preferred). */
  getStreamUrl?: () => Promise<string>;
  label: string;
}

export interface UseSquawkStreamResult {
  label: string;
  status: SquawkStatus;
  volume: number;
  sdkReady: boolean;
  isPlaying: boolean;
  /** User intends this stream to be live (survives background disconnect). */
  intendedLive: boolean;
  lastError: string | null;
  start: () => Promise<void>;
  stop: () => void;
  setVolume: (v: number) => void;
  /** Reconnect if intendedLive and not currently live. */
  resumeIfNeeded: () => Promise<void>;
}

function sliderToGain(value: number): number {
  const v = Math.max(0, Math.min(100, value));
  if (v === 0) return 0;
  const minDb = -60;
  const t = (v - 1) / 99;
  const db = minDb * (1 - t);
  return Math.pow(10, db / 20);
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

/**
 * Imperative SRS WebRTC player for mobile shell.
 * Does not unmount on tab change when used from MobileAudioProvider.
 */
export function useSquawkStream(
  options: UseSquawkStreamOptions,
): UseSquawkStreamResult {
  const { streamUrl, getStreamUrl, label } = options;

  const [status, setStatus] = useState<SquawkStatus>('disconnected');
  const [volume, setVolumeState] = useState(100);
  const [sdkReady, setSdkReady] = useState(false);
  const [intendedLive, setIntendedLive] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const sdkRef = useRef<ReturnType<typeof window.SrsRtcPlayerAsync> | null>(
    null,
  );
  const gainRef = useRef<GainNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const volumeRef = useRef(volume);
  const intendedLiveRef = useRef(false);
  const startInFlightRef = useRef(false);
  const statusRef = useRef<SquawkStatus>('disconnected');
  const connectStartedAtRef = useRef(0);
  const startGenRef = useRef(0);

  volumeRef.current = volume;
  intendedLiveRef.current = intendedLive;
  statusRef.current = status;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await ensureSrsSdk();
        if (!cancelled) setSdkReady(true);
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setLastError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const teardownPc = useCallback(() => {
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        /* noop */
      }
      sourceRef.current = null;
    }
    if (sdkRef.current) {
      try {
        sdkRef.current.close();
      } catch {
        /* noop */
      }
      sdkRef.current = null;
    }
    if (audioElRef.current) {
      audioElRef.current.srcObject = null;
    }
    // Do not close shared AudioContext — the other stream may still use it.
  }, []);

  useEffect(() => {
    return () => {
      startGenRef.current += 1;
      teardownPc();
      if (audioElRef.current?.parentNode) {
        audioElRef.current.parentNode.removeChild(audioElRef.current);
        audioElRef.current = null;
      }
      gainRef.current = null;
    };
  }, [teardownPc]);

  const start = useCallback(async () => {
    if (!sdkReady) return;
    // Allow retry even if a previous start hung: only block parallel starts.
    if (startInFlightRef.current) return;

    const gen = ++startGenRef.current;
    startInFlightRef.current = true;
    setIntendedLive(true);
    intendedLiveRef.current = true;
    setLastError(null);
    setStatus('connecting');
    statusRef.current = 'connecting';
    connectStartedAtRef.current = Date.now();

    teardownPc();

    try {
      await ensureSrsSdk();
      if (gen !== startGenRef.current) return;

      const sdk = new window.SrsRtcPlayerAsync();
      sdkRef.current = sdk;

      // Audio-only offer — same fix as desktop SquawkPlayer (zeroday video
      // keyframes starve Opus on a shared transport).
      const pc = sdk.pc as RTCPeerConnection | undefined;
      if (pc) {
        const addTransceiver = pc.addTransceiver.bind(pc);
        pc.addTransceiver = ((kind: string, init?: RTCRtpTransceiverInit) =>
          kind === 'video'
            ? undefined
            : addTransceiver(kind, init)) as RTCPeerConnection['addTransceiver'];
      }

      if (!audioElRef.current) {
        const el = document.createElement('audio');
        el.autoplay = true;
        el.setAttribute('playsinline', 'true');
        el.setAttribute('webkit-playsinline', 'true');
        el.style.position = 'fixed';
        el.style.width = '1px';
        el.style.height = '1px';
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
        el.volume = 0;
        document.body.appendChild(el);
        audioElRef.current = el;
      }
      audioElRef.current.srcObject = sdk.stream;
      audioElRef.current.volume = 0;

      const audioCtx = getSharedAudioContext();
      await resumeSharedAudioContext();
      if (gen !== startGenRef.current) return;

      if (!gainRef.current) {
        gainRef.current = audioCtx.createGain();
        gainRef.current.connect(audioCtx.destination);
      }

      const vol = volumeRef.current;
      gainRef.current.gain.value = sliderToGain(vol);

      const url = getStreamUrl ? await getStreamUrl() : streamUrl;
      if (gen !== startGenRef.current) return;

      await withTimeout(
        sdk.play(url) as Promise<unknown>,
        SQUAWK_CONNECT_TIMEOUT_MS,
        `${label} WebRTC play`,
      );
      if (gen !== startGenRef.current) return;

      // Fire-and-forget: never await this. The element is muted (volume 0) and
      // only exists to keep the MediaStream alive — Web Audio below does the
      // actual output — so its playback state is not something to block on.
      // Awaiting it deadlocks the connect: with a live WebRTC srcObject and a
      // muted element, Chrome can leave this promise pending indefinitely, and
      // a try/catch does not help because the promise never settles. Measured
      // hanging ~15s until the watchdog's teardown nulled srcObject and it
      // rejected AbortError — while ICE had been connected the whole time.
      void audioElRef.current.play().catch(() => {
        /* gesture-driven start; element may still feed Web Audio */
      });

      // Re-check after every await: Stop must win over a late play() resolve.
      if (gen !== startGenRef.current || !intendedLiveRef.current) {
        teardownPc();
        return;
      }

      sourceRef.current = audioCtx.createMediaStreamSource(sdk.stream);
      if (vol > 0) {
        sourceRef.current.connect(gainRef.current);
      }

      if (gen !== startGenRef.current || !intendedLiveRef.current) {
        teardownPc();
        return;
      }

      const next: SquawkStatus = vol === 0 ? 'muted' : 'live';
      setStatus(next);
      statusRef.current = next;
    } catch (err) {
      if (gen !== startGenRef.current) return;
      teardownPc();
      // User cancelled via Stop — stay disconnected, not error.
      if (!intendedLiveRef.current) {
        setStatus('disconnected');
        statusRef.current = 'disconnected';
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setLastError(msg);
      setStatus('error');
      statusRef.current = 'error';
    } finally {
      // Always release the mutex, even when this generation was superseded.
      // Gating on `gen === startGenRef.current` deadlocks the hook: a start that
      // loses the race returns from inside `try`, skips the reset, and leaves
      // startInFlightRef stuck true — after which the guard at the top of
      // start() rejects every retry and status is pinned on 'connecting'
      // forever. Generation still gates the *status writes* above; it must not
      // gate the lock.
      startInFlightRef.current = false;
    }
  }, [sdkReady, teardownPc, streamUrl, getStreamUrl, label]);

  const stop = useCallback(() => {
    // Invalidate any in-flight start before teardown so late play() cannot
    // flip status back to live (common when Stop is pressed mid-connect).
    startGenRef.current += 1;
    startInFlightRef.current = false;
    setIntendedLive(false);
    intendedLiveRef.current = false;
    setLastError(null);
    teardownPc();
    setStatus('disconnected');
    statusRef.current = 'disconnected';
  }, [teardownPc]);

  const setVolume = useCallback(
    (newVolume: number) => {
      setVolumeState(newVolume);
      volumeRef.current = newVolume;

      if (newVolume === 0) {
        if (sdkRef.current) {
          teardownPc();
          setStatus('muted');
          statusRef.current = 'muted';
        } else if (intendedLiveRef.current) {
          setStatus('muted');
          statusRef.current = 'muted';
        }
        return;
      }

      if (gainRef.current) {
        gainRef.current.gain.value = sliderToGain(newVolume);
      }

      if (!sdkRef.current && intendedLiveRef.current) {
        void start();
      } else if (sdkRef.current) {
        setStatus('live');
        statusRef.current = 'live';
      }
    },
    [teardownPc, start],
  );

  const resumeIfNeeded = useCallback(async () => {
    if (!intendedLiveRef.current) return;
    const st = statusRef.current;
    if (st === 'live') return;
    // If still "connecting" but past timeout, force a retry.
    if (st === 'connecting') {
      const age = Date.now() - connectStartedAtRef.current;
      if (age < SQUAWK_CONNECT_TIMEOUT_MS && startInFlightRef.current) {
        return;
      }
      // Stale connect — allow start() again.
      startInFlightRef.current = false;
    }
    if (volumeRef.current === 0) {
      setStatus('muted');
      statusRef.current = 'muted';
      return;
    }
    try {
      await resumeSharedAudioContext();
    } catch {
      /* ignore */
    }
    await start();
  }, [start]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void resumeIfNeeded();
      }
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void resumeIfNeeded();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [resumeIfNeeded]);

  // Watchdog: if the UI sits on "connecting" past the timeout, surface an error.
  // Deliberately NOT gated on startInFlightRef — that check made the watchdog
  // useless in the exact case it exists for (a start whose own timeout never
  // resolved leaves the flag set, so the rescue was suppressed by the symptom).
  // The in-flight start owns its own withTimeout; this is the outer backstop.
  useEffect(() => {
    if (status !== 'connecting') return;
    const t = window.setTimeout(() => {
      if (statusRef.current !== 'connecting') return;
      // Invalidate the stalled attempt so a retry is not rejected as parallel.
      startGenRef.current += 1;
      startInFlightRef.current = false;
      teardownPc();
      setLastError('Connection stalled');
      setStatus('error');
      statusRef.current = 'error';
    }, SQUAWK_CONNECT_TIMEOUT_MS + 500);
    return () => window.clearTimeout(t);
  }, [status, teardownPc]);

  const isPlaying = status === 'live' || status === 'connecting';

  return {
    label,
    status,
    volume,
    sdkReady,
    isPlaying,
    intendedLive,
    lastError,
    start,
    stop,
    setVolume,
    resumeIfNeeded,
  };
}
